import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { 
  History, 
  Play, 
  XCircle, 
  KeyRound, 
  Gift, 
  Loader2,
  Clock,
  RefreshCw,
  Filter,
  ShoppingCart,
  Sparkles,
  User,
  Trash2,
  UserPlus
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface AdminAction {
  id: string;
  action_type: string;
  admin_user_id: string;
  target_user_id: string;
  details: Record<string, unknown> | null;
  created_at: string;
}

interface ActivityTimelineProps {
  isAdmin?: boolean;
  currentUserId?: string;
}

const actionConfig: Record<string, { 
  icon: React.ComponentType<{ className?: string }>; 
  label: string; 
  color: string;
  bgColor: string;
}> = {
  membership_activate: {
    icon: Play,
    label: 'Membresía reactivada',
    color: 'text-green-400',
    bgColor: 'bg-green-500/20',
  },
  membership_activation: {
    icon: Sparkles,
    label: 'Membresía activada',
    color: 'text-emerald-400',
    bgColor: 'bg-emerald-500/20',
  },
  membership_deactivate: {
    icon: XCircle,
    label: 'Membresía pausada',
    color: 'text-red-400',
    bgColor: 'bg-red-500/20',
  },
  membership_renewal: {
    icon: RefreshCw,
    label: 'Membresía renovada',
    color: 'text-blue-400',
    bgColor: 'bg-blue-500/20',
  },
  pin_change: {
    icon: KeyRound,
    label: 'PIN actualizado',
    color: 'text-cyan-400',
    bgColor: 'bg-cyan-500/20',
  },
  benefit_redemption: {
    icon: Gift,
    label: 'Canje registrado',
    color: 'text-purple-400',
    bgColor: 'bg-purple-500/20',
  },
  discount_purchase: {
    icon: ShoppingCart,
    label: 'Compra con descuento',
    color: 'text-green-400',
    bgColor: 'bg-green-500/20',
  },
  member_deleted: {
    icon: XCircle,
    label: 'Miembro eliminado',
    color: 'text-red-400',
    bgColor: 'bg-red-500/20',
  },
  team_member_created: {
    icon: User,
    label: 'Vendedor creado',
    color: 'text-blue-400',
    bgColor: 'bg-blue-500/20',
  },
  team_member_deleted: {
    icon: XCircle,
    label: 'Vendedor eliminado',
    color: 'text-red-400',
    bgColor: 'bg-red-500/20',
  },
  // Prize-related actions
  prize_created: {
    icon: Gift,
    label: 'Premio creado',
    color: 'text-amber-400',
    bgColor: 'bg-amber-500/20',
  },
  prize_updated: {
    icon: Gift,
    label: 'Premio actualizado',
    color: 'text-amber-400',
    bgColor: 'bg-amber-500/20',
  },
  prize_assigned: {
    icon: Gift,
    label: 'Premio asignado',
    color: 'text-green-400',
    bgColor: 'bg-green-500/20',
  },
  prize_claimed: {
    icon: Gift,
    label: 'Premio reclamado',
    color: 'text-purple-400',
    bgColor: 'bg-purple-500/20',
  },
  prize_delivered: {
    icon: Gift,
    label: 'Premio entregado',
    color: 'text-emerald-400',
    bgColor: 'bg-emerald-500/20',
  },
  // Deletion actions
  purchase_deleted: {
    icon: Trash2,
    label: 'Compra eliminada',
    color: 'text-orange-400',
    bgColor: 'bg-orange-500/20',
  },
  redemption_deleted: {
    icon: Trash2,
    label: 'Canje eliminado',
    color: 'text-orange-400',
    bgColor: 'bg-orange-500/20',
  },
  // Registration action
  member_registered: {
    icon: UserPlus,
    label: 'Nuevo miembro registrado',
    color: 'text-sky-400',
    bgColor: 'bg-sky-500/20',
  },
};

export const ActivityTimeline: React.FC<ActivityTimelineProps> = ({ 
  isAdmin = false, 
  currentUserId 
}) => {
  const [actions, setActions] = useState<AdminAction[]>([]);
  const [adminNames, setAdminNames] = useState<Record<string, string>>({});
  const [targetNames, setTargetNames] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('todos');

  const fetchActions = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('admin_audit_logs')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(isAdmin ? 100 : 50);

      // Vendedores: solo sus propias acciones
      if (!isAdmin && currentUserId) {
        query = query.eq('admin_user_id', currentUserId);
      }

      if (typeFilter !== 'todos') {
        query = query.eq('action_type', typeFilter);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching actions:', error);
        return;
      }

      const actionsData = (data as AdminAction[]) || [];
      setActions(actionsData);

      // Fetch all names
      const uniqueAdminIds = [...new Set(actionsData.map(a => a.admin_user_id))];
      const uniqueTargetIds = [...new Set(actionsData.map(a => a.target_user_id))];
      const allIds = [...new Set([...uniqueAdminIds, ...uniqueTargetIds])];

      if (allIds.length > 0) {
        const { data: profiles } = await supabase
          .from('profiles')
          .select('user_id, nombre')
          .in('user_id', allIds);
        
        if (profiles) {
          const namesMap: Record<string, string> = {};
          profiles.forEach(p => {
            namesMap[p.user_id] = p.nombre;
          });
          setAdminNames(namesMap);
          setTargetNames(namesMap);
        }
      }
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchActions();
  }, [typeFilter, isAdmin, currentUserId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with title based on role */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-white flex items-center gap-2">
          <History className="w-5 h-5 text-blue-400" />
          {isAdmin ? 'Actividad del Sistema' : 'Mi Actividad'}
        </h2>
      </div>

      {/* Filter bar */}
      <div className="flex items-center gap-3">
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="bg-white/10 border-white/20 text-white w-48">
            <Filter className="w-4 h-4 mr-2 text-white/50" />
            <SelectValue placeholder="Filtrar por tipo" />
          </SelectTrigger>
          <SelectContent className="bg-slate-800 border-white/20">
            <SelectItem value="todos" className="text-white hover:bg-white/10">Todas las acciones</SelectItem>
            <SelectItem value="member_registered" className="text-sky-400 hover:bg-white/10">Registros</SelectItem>
            <SelectItem value="benefit_redemption" className="text-purple-400 hover:bg-white/10">Canjes</SelectItem>
            <SelectItem value="discount_purchase" className="text-green-400 hover:bg-white/10">Compras con descuento</SelectItem>
            <SelectItem value="membership_activation" className="text-emerald-400 hover:bg-white/10">Activaciones</SelectItem>
            <SelectItem value="membership_activate" className="text-green-400 hover:bg-white/10">Reactivaciones</SelectItem>
            <SelectItem value="membership_deactivate" className="text-red-400 hover:bg-white/10">Pausas</SelectItem>
            <SelectItem value="membership_renewal" className="text-blue-400 hover:bg-white/10">Renovaciones</SelectItem>
            <SelectItem value="pin_change" className="text-cyan-400 hover:bg-white/10">Cambios de PIN</SelectItem>
            <SelectItem value="prize_created" className="text-amber-400 hover:bg-white/10">Premios creados</SelectItem>
            <SelectItem value="prize_assigned" className="text-green-400 hover:bg-white/10">Premios asignados</SelectItem>
            <SelectItem value="prize_claimed" className="text-purple-400 hover:bg-white/10">Premios reclamados</SelectItem>
            <SelectItem value="prize_delivered" className="text-emerald-400 hover:bg-white/10">Premios entregados</SelectItem>
            <SelectItem value="purchase_deleted" className="text-orange-400 hover:bg-white/10">Compras eliminadas</SelectItem>
            <SelectItem value="redemption_deleted" className="text-orange-400 hover:bg-white/10">Canjes eliminados</SelectItem>
            {isAdmin && (
              <>
                <SelectItem value="team_member_created" className="text-blue-400 hover:bg-white/10">Vendedores creados</SelectItem>
                <SelectItem value="team_member_deleted" className="text-red-400 hover:bg-white/10">Vendedores eliminados</SelectItem>
                <SelectItem value="member_deleted" className="text-red-400 hover:bg-white/10">Miembros eliminados</SelectItem>
              </>
            )}
          </SelectContent>
        </Select>

        <Button
          variant="outline"
          size="sm"
          onClick={fetchActions}
          className="border-blue-500/30 text-blue-400 hover:bg-blue-500/20"
        >
          <RefreshCw className="w-4 h-4 mr-1" />
          Actualizar
        </Button>
      </div>

      {/* Timeline */}
      {actions.length === 0 ? (
        <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-8 border border-white/10 text-center">
          <History className="w-12 h-12 text-white/20 mx-auto mb-3" />
          <p className="text-white/50">
            {isAdmin ? 'No hay acciones registradas en el sistema' : 'No has realizado acciones aún'}
          </p>
        </div>
      ) : (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="bg-white/5 backdrop-blur-xl rounded-2xl p-4 border border-white/10"
        >
          <div className="space-y-3 max-h-[calc(100vh-300px)] overflow-y-auto pr-2">
            {actions.map((action, index) => {
              const config = actionConfig[action.action_type] || {
                icon: Clock,
                label: action.action_type,
                color: 'text-white/70',
                bgColor: 'bg-white/10',
              };
              const IconComponent = config.icon;
              const adminFullName = adminNames[action.admin_user_id] || 'Admin';
              const adminName = !isAdmin && action.admin_user_id === currentUserId 
                ? 'Yo' 
                : adminFullName.split(' ')[0];
              const targetName = targetNames[action.target_user_id] || 'Usuario';
              const details = action.details as Record<string, unknown> | null;

              return (
                <motion.div
                  key={action.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.03 }}
                  className="flex items-start gap-4 p-4 rounded-xl bg-white/5 border border-white/5 hover:bg-white/10 transition-colors"
                >
                  <div className={`p-2.5 rounded-xl ${config.bgColor} shrink-0`}>
                    <IconComponent className={`w-5 h-5 ${config.color}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="text-white font-medium">{config.label}</p>
                        <p className="text-white/60 text-sm">
                          <span className="text-blue-400">{adminName}</span>
                          {' → '}
                          <span className="text-white/80">{targetName}</span>
                        </p>
                      </div>
                      <span className="text-white/40 text-xs whitespace-nowrap">
                        {format(new Date(action.created_at), "d MMM, HH:mm", { locale: es })}
                      </span>
                    </div>

                    {/* Detailed info for benefit redemptions */}
                    {details && action.action_type === 'benefit_redemption' && (
                      <div className="mt-2 text-white/50 text-xs">
                        <p>
                          {(details.quantity as number) || 1}x {' '}
                          {details.benefit_type as string}
                        </p>
                        {details.notes && (
                          <p className="text-white/40 italic mt-1">"{details.notes as string}"</p>
                        )}
                      </div>
                    )}

                    {/* Detailed info for discount purchases */}
                    {details && action.action_type === 'discount_purchase' && (
                      <div className="mt-2">
                        <p className="text-white/50 text-xs">
                          {details.product_name as string}
                          {' • '}
                          <span className="text-white/40">{details.category as string}</span>
                          {' • '}
                          <span className="text-green-400">
                            -{details.discount_percentage as number}%
                          </span>
                        </p>
                        {isAdmin && (
                          <div className="mt-2 p-2 bg-white/5 rounded-lg text-xs grid grid-cols-2 gap-2">
                            <div>
                              <span className="text-white/40">Original:</span>{' '}
                              <span className="text-white/70">₡{(details.original_price as number)?.toLocaleString()}</span>
                            </div>
                            <div>
                              <span className="text-white/40">Final:</span>{' '}
                              <span className="text-green-400">₡{(details.final_price as number)?.toLocaleString()}</span>
                            </div>
                            <div className="col-span-2">
                              <span className="text-white/40">Ahorro cliente:</span>{' '}
                              <span className="text-amber-400">₡{(details.savings as number)?.toLocaleString()}</span>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Detailed info for membership activation */}
                    {details && action.action_type === 'membership_activation' && (
                      <div className="mt-2 p-2 bg-white/5 rounded-lg text-xs space-y-1">
                        <p>
                          <span className="text-white/40">Plan:</span>{' '}
                          <span className="text-amber-400 capitalize">{details.plan as string}</span>
                        </p>
                        {isAdmin && (
                          <>
                            <p>
                              <span className="text-white/40">Período:</span>{' '}
                              <span className="text-white/70 capitalize">{details.billing_period as string}</span>
                            </p>
                            <p>
                              <span className="text-white/40">Monto:</span>{' '}
                              <span className="text-green-400">₡{(details.amount as number)?.toLocaleString()}</span>
                            </p>
                            {details.sold_by_name && (
                              <p>
                                <span className="text-white/40">Vendedor:</span>{' '}
                                <span className="text-cyan-400">{details.sold_by_name as string}</span>
                              </p>
                            )}
                          </>
                        )}
                        {details.points_awarded && (
                          <p>
                            <span className="text-white/40">Puntos ganados:</span>{' '}
                            <span className="text-yellow-400 font-semibold">+{details.points_awarded as number} pts</span>
                          </p>
                        )}
                        {isAdmin && details.notes && (
                          <p>
                            <span className="text-white/40">Notas:</span>{' '}
                            <span className="text-white/60 italic">"{details.notes as string}"</span>
                          </p>
                        )}
                      </div>
                    )}

                    {/* Detailed info for membership renewal */}
                    {details && action.action_type === 'membership_renewal' && (
                      <div className="mt-2 text-white/50 text-xs">
                        <p>
                          <span className="text-white/40">Período:</span>{' '}
                          <span className="text-white/70 capitalize">{details.billing_period as string}</span>
                        </p>
                        {isAdmin && details.amount && (
                          <p>
                            <span className="text-white/40">Monto:</span>{' '}
                            <span className="text-green-400">₡{(details.amount as number)?.toLocaleString()}</span>
                          </p>
                        )}
                      </div>
                    )}

                    {/* Detailed info for PIN change */}
                    {action.action_type === 'pin_change' && isAdmin && (
                      <p className="text-white/40 text-xs mt-1">PIN actualizado por admin</p>
                    )}

                    {/* Detailed info for team member creation */}
                    {details && action.action_type === 'team_member_created' && isAdmin && (
                      <div className="mt-2 text-white/50 text-xs">
                        <p>
                          <span className="text-white/40">Tienda:</span>{' '}
                          <span className="text-white/70">{details.ubicacion_tienda as string || 'No asignada'}</span>
                        </p>
                      </div>
                    )}

                    {/* Detailed info for membership deactivate/activate */}
                    {details && (action.action_type === 'membership_deactivate' || action.action_type === 'membership_activate') && details.reason && (
                      <p className="text-white/40 text-xs mt-1 italic">"{details.reason as string}"</p>
                    )}

                    {/* Detailed info for member registration */}
                    {details && action.action_type === 'member_registered' && (
                      <div className="mt-2 p-2 bg-white/5 rounded-lg text-xs space-y-1">
                        <p>
                          <span className="text-white/40">📱 Plan:</span>{' '}
                          <span className="text-amber-400 capitalize">{details.plan as string}</span>
                          {' '}
                          <span className="text-white/50">({details.billing_period as string})</span>
                        </p>
                        {details.telefono && (
                          <p>
                            <span className="text-white/40">📞 Teléfono:</span>{' '}
                            <span className="text-white/70">{details.telefono as string}</span>
                          </p>
                        )}
                        {details.modelo_celular && (
                          <p>
                            <span className="text-white/40">📲 Modelo:</span>{' '}
                            <span className="text-white/70">{details.modelo_celular as string}</span>
                          </p>
                        )}
                      </div>
                    )}

                    {/* Detailed info for prize actions */}
                    {details && (action.action_type === 'prize_created' || action.action_type === 'prize_updated') && (
                      <div className="mt-2 text-white/50 text-xs">
                        <p>
                          <span className="text-white/40">Premio:</span>{' '}
                          <span className="text-amber-400">{details.prize_name as string}</span>
                        </p>
                        {details.prize_value && (
                          <p>
                            <span className="text-white/40">Valor:</span>{' '}
                            <span className="text-white/70">₡{(details.prize_value as number)?.toLocaleString()}</span>
                          </p>
                        )}
                      </div>
                    )}

                    {details && action.action_type === 'prize_assigned' && (
                      <div className="mt-2 text-white/50 text-xs">
                        <p>
                          <span className="text-white/40">Ganador:</span>{' '}
                          <span className="text-amber-400">{details.winner_name as string}</span>
                        </p>
                      </div>
                    )}

                    {details && action.action_type === 'prize_delivered' && (
                      <div className="mt-2 text-white/50 text-xs">
                        <p>
                          <span className="text-white/40">Premio:</span>{' '}
                          <span className="text-green-400">{details.prize_name as string}</span>
                        </p>
                      </div>
                    )}

                    {details && action.action_type === 'prize_claimed' && (
                      <div className="mt-2 text-white/50 text-xs">
                        <p>
                          <span className="text-white/40">Premio:</span>{' '}
                          <span className="text-purple-400">{details.prize_name as string}</span>
                        </p>
                        {details.claim_code && (
                          <p>
                            <span className="text-white/40">Código:</span>{' '}
                            <span className="text-white/70">{details.claim_code as string}</span>
                          </p>
                        )}
                      </div>
                    )}

                    {/* Detailed info for deleted purchases */}
                    {details && action.action_type === 'purchase_deleted' && (
                      <div className="mt-2 p-2 bg-orange-500/10 rounded-lg border border-orange-500/20">
                        <p className="text-white/60 text-xs">
                          <span className="text-orange-400">{details.product_name as string}</span>
                          {details.category && (
                            <>
                              {' • '}
                              <span className="text-white/40">{details.category as string}</span>
                            </>
                          )}
                        </p>
                        {details.reason && (
                          <p className="text-orange-300/80 text-xs mt-1 italic">
                            💬 "{details.reason as string}"
                          </p>
                        )}
                      </div>
                    )}

                    {/* Detailed info for deleted redemptions */}
                    {details && action.action_type === 'redemption_deleted' && (
                      <div className="mt-2 p-2 bg-orange-500/10 rounded-lg border border-orange-500/20">
                        <p className="text-white/60 text-xs">
                          {(details.quantity as number) || 1}x {details.benefit_type as string}
                        </p>
                        {details.reason && (
                          <p className="text-orange-300/80 text-xs mt-1 italic">
                            💬 "{details.reason as string}"
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default ActivityTimeline;
